import type { AuthResponse, MemberProfile, MemberUser, RoleDefinition, UserProfile } from '@/lib/api';
import { apiRequest } from '@/lib/api';

export function registerUser(nickname: string, password: string) {
  return apiRequest<AuthResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ nickname, password }),
  });
}

export function loginUser(nickname: string, password: string) {
  return apiRequest<AuthResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ nickname, password }),
  });
}

export function fetchCurrentUser(token: string) {
  return apiRequest<{ user: UserProfile }>('/api/auth/me', {}, token);
}

export function updateProfile(
  token: string,
  data: { nickname?: string; about?: string | null; avatar?: string | null },
) {
  return apiRequest<{ user: UserProfile; token: string }>(
    '/api/users/me',
    { method: 'PATCH', body: JSON.stringify(data) },
    token,
  );
}

export function changePassword(token: string, oldPassword: string, newPassword: string) {
  return apiRequest<{ ok: boolean }>(
    '/api/users/me/password',
    { method: 'PATCH', body: JSON.stringify({ oldPassword, newPassword }) },
    token,
  );
}

export function fetchRoles(token: string) {
  return apiRequest<{ roles: RoleDefinition[] }>('/api/roles', {}, token);
}

export function fetchMembers(token: string) {
  return apiRequest<{ members: MemberUser[] }>('/api/members', {}, token);
}

export function fetchMember(token: string, userId: number) {
  return apiRequest<{ member: MemberProfile }>(`/api/members/${userId}`, {}, token);
}

export function toggleAvatarLike(token: string, userId: number) {
  return apiRequest<{ liked: boolean; avatarLikesCount: number }>(
    `/api/members/${userId}/avatar-like`,
    { method: 'POST' },
    token,
  );
}

export function fetchMonthEvents(token: string) {
  return apiRequest<{ events: import('@/types/api').PlatformEvent[] }>('/api/events/month', {}, token);
}

export function fetchPlatformStats(token: string) {
  return apiRequest<import('@/types/api').PlatformStats>('/api/platform/stats', {}, token);
}

export function fetchWallPosts(token: string) {
  return apiRequest<import('@/types/api').WallPostsResponse>('/api/wall/posts', {}, token);
}

export function createWallPost(token: string, data: { message?: string; drawing?: string | null }) {
  return apiRequest<{ post: import('@/types/api').WallPostCore; author: import('@/types/api').WallAuthor }>(
    '/api/wall/posts',
    { method: 'POST', body: JSON.stringify(data) },
    token,
  );
}

export function reactToPost(token: string, postId: number, emoji: string) {
  return apiRequest<{ reactions: { emoji: string; count: number }[] }>(
    `/api/wall/posts/${postId}/react`,
    { method: 'POST', body: JSON.stringify({ emoji }) },
    token,
  );
}

export function fetchGifts(token: string) {
  return apiRequest<{ gifts: import('@/types/api').VirtualGift[] }>('/api/gifts', {}, token);
}

export function fetchMemberGifts(token: string, userId: number) {
  return apiRequest<{ gifts: import('@/types/api').VirtualGift[] }>(
    `/api/members/${userId}/gifts`,
    {},
    token,
  );
}

export function sendGift(
  token: string,
  data: {
    giftType: string;
    message?: string;
    toUserId: number;
    isPublic?: boolean;
    isAnonymous?: boolean;
  },
) {
  return apiRequest('/api/gifts', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function fetchMemberWallPosts(token: string, userId: number) {
  return apiRequest<{ posts: import('@/types/api').ProfileWallPost[] }>(
    `/api/members/${userId}/wall`,
    {},
    token,
  );
}

export function createMemberWallPost(
  token: string,
  userId: number,
  data: { message?: string; drawing?: string | null },
) {
  return apiRequest(
    `/api/members/${userId}/wall`,
    { method: 'POST', body: JSON.stringify(data) },
    token,
  );
}

export function fetchAdminUsers(token: string) {
  return apiRequest<{ users: UserProfile[] }>('/api/admin/users', {}, token);
}

export function createEvent(
  token: string,
  data: { title: string; description?: string; eventDate: string; emoji?: string },
) {
  return apiRequest('/api/admin/events', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function fetchAdminEvents(token: string) {
  return apiRequest<{ events: import('@/types/api').PlatformEvent[] }>('/api/admin/events', {}, token);
}

export function deleteEvent(token: string, eventId: number) {
  return apiRequest<{ ok: boolean }>(`/api/admin/events/${eventId}`, { method: 'DELETE' }, token);
}

export function updateUserRole(token: string, userId: number, role: string) {
  return apiRequest<{ user: UserProfile }>(
    `/api/admin/users/${userId}/role`,
    { method: 'PATCH', body: JSON.stringify({ role }) },
    token,
  );
}

export function updateRoleLabel(
  token: string,
  key: string,
  data: { label: string; badgeColor?: string },
) {
  return apiRequest<{ role: RoleDefinition }>(
    `/api/admin/roles/${key}`,
    { method: 'PATCH', body: JSON.stringify(data) },
    token,
  );
}

export function recordPlaygroundClick(token: string, category: string) {
  return apiRequest<{ scores: import('@/types/api').PlaygroundScores }>(
    '/api/playground/click',
    { method: 'POST', body: JSON.stringify({ category }) },
    token,
  );
}

export function savePlaygroundScores(
  token: string,
  data: {
    money?: number;
    health?: number;
    love?: number;
    bonusMoney?: number[];
    bonusHealth?: number[];
    bonusLove?: number[];
  },
) {
  return apiRequest<{ scores: import('@/types/api').PlaygroundScores }>(
    '/api/playground/save',
    { method: 'POST', body: JSON.stringify(data) },
    token,
  );
}

export function fetchBonusLootTiers(token: string) {
  return apiRequest<{ tiers: import('@/types/api').BonusLootTier[] }>(
    '/api/playground/bonus-loot',
    {},
    token,
  );
}

export function fetchAdminBonusLootTiers(token: string) {
  return apiRequest<{ tiers: import('@/types/api').BonusLootTier[] }>(
    '/api/admin/bonus-loot',
    {},
    token,
  );
}

export function saveAdminBonusLootTiers(
  token: string,
  tiers: import('@/types/api').BonusLootTierInput[],
) {
  return apiRequest<{ tiers: import('@/types/api').BonusLootTier[] }>(
    '/api/admin/bonus-loot',
    {
      method: 'PATCH',
      body: JSON.stringify({
        tiers: tiers.map((tier) => ({
          rewardAmount: Math.round(Number(tier.rewardAmount)),
          chancePercent: Number(tier.chancePercent),
        })),
      }),
    },
    token,
  );
}

export function fetchPlaygroundScores(token: string) {
  return apiRequest<{ scores: import('@/types/api').PlaygroundScores }>(
    '/api/playground/scores',
    {},
    token,
  );
}

export function fetchPlaygroundLeaders(token: string) {
  return apiRequest<{ leaders: import('@/types/api').PlaygroundLeader[] }>(
    '/api/playground/leaders',
    {},
    token,
  );
}

export function fetchPlaygroundTotals(token: string) {
  return apiRequest<{ totals: import('@/types/api').PlaygroundScores }>(
    '/api/playground/totals',
    {},
    token,
  );
}

export function fetchAdminPlaygroundScores(token: string) {
  return apiRequest<{ scores: import('@/types/api').PlaygroundUserScores[] }>(
    '/api/admin/playground/scores',
    {},
    token,
  );
}

export function updateAdminUserScores(
  token: string,
  userId: number,
  data: { money: number; health: number; love: number },
) {
  return apiRequest<{ scores: import('@/types/api').PlaygroundScores }>(
    `/api/admin/playground/users/${userId}/scores`,
    { method: 'PATCH', body: JSON.stringify(data) },
    token,
  );
}

export function fetchQuestState(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>('/api/quest/state', {}, token);
}

export function joinQuest(token: string, playerClass?: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/join',
    { method: 'POST', body: JSON.stringify({ playerClass }) },
    token,
  );
}

export function contributeQuest(
  token: string,
  data: { hp?: number; gold?: number; love?: number },
) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/contribute',
    { method: 'POST', body: JSON.stringify(data) },
    token,
  );
}

export function syncQuestFromClicker(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/sync',
    { method: 'POST', body: JSON.stringify({}) },
    token,
  );
}

export function continueQuest(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/continue',
    { method: 'POST', body: JSON.stringify({}) },
    token,
  );
}

export function submitQuestBossQueue(token: string, order: number[]) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/boss/queue',
    { method: 'POST', body: JSON.stringify({ order }) },
    token,
  );
}

export function submitQuestBossSubmit(
  token: string,
  data: { clicks: number; bonusAmounts: number[]; turnStartedAt?: string },
) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/boss/submit',
    { method: 'POST', body: JSON.stringify(data) },
    token,
  );
}

export function submitQuestBossClick(token: string) {
  return submitQuestBossSubmit(token, { clicks: 1, bonusAmounts: [] });
}

export function buyQuestWeapon(token: string, weaponId: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/shop/buy',
    { method: 'POST', body: JSON.stringify({ weaponId }) },
    token,
  );
}

export function fetchQuestAdminConfig(token: string) {
  return apiRequest<{
    config: import('@/types/api').QuestAdminConfig;
    scenes: import('@/types/api').QuestSceneConfig[];
    activeRun: { id: number; status: string } | null;
  }>('/api/quest/admin/config', {}, token);
}

export function patchQuestAdminConfig(
  token: string,
  data: {
    finalNote?: string;
    durationMinutes?: number;
    tickMinutes?: number;
    bossMaxHp?: number;
    bossPhase2Hp?: number;
    weaponDoubleGoldCost?: number;
    weaponCritGoldCost?: number;
    weaponTimeGoldCost?: number;
  },
) {
  return apiRequest<{
    config: import('@/types/api').QuestAdminConfig;
    scenes: import('@/types/api').QuestSceneConfig[];
    activeRun: { id: number; status: string } | null;
  }>('/api/quest/admin/config', { method: 'PATCH', body: JSON.stringify(data) }, token);
}

export function patchQuestScene(
  token: string,
  sceneIndex: number,
  data: {
    hpCost?: number;
    goldCost?: number;
    loveCost?: number;
    narrative?: string;
    diceMax?: number;
    diceThreshold?: number;
  },
) {
  return apiRequest<{
    config: import('@/types/api').QuestAdminConfig;
    scenes: import('@/types/api').QuestSceneConfig[];
    activeRun: { id: number; status: string } | null;
  }>(
    `/api/quest/admin/scenes/${sceneIndex}`,
    { method: 'PATCH', body: JSON.stringify(data) },
    token,
  );
}

export function startQuest(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/admin/start',
    { method: 'POST' },
    token,
  );
}

export function failQuest(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/admin/fail',
    { method: 'POST' },
    token,
  );
}

export function divineQuest(token: string, action: string, amount?: number) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/admin/divine',
    { method: 'POST', body: JSON.stringify({ action, amount }) },
    token,
  );
}

export function advanceQuestScene(token: string) {
  return apiRequest<{ state: import('@/types/api').QuestState }>(
    '/api/quest/admin/advance',
    { method: 'POST' },
    token,
  );
}
